/** Shared helpers for V3 itinerary rendering. */

/**
 * Convert minutes-from-midnight to human-readable time string.
 * e.g. 540 → "9:00 AM", 1320 → "10:00 PM"
 */
export function formatTime(minutes) {
  if (minutes == null || minutes < 0) return '';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${display}:${String(m).padStart(2, '0')} ${period}`;
}

/**
 * Format a time range from startMin/endMin.
 * e.g. "9:00 AM - 9:45 AM"
 */
export function formatTimeRange(startMin, endMin) {
  if (startMin == null) return '';
  const start = formatTime(startMin);
  if (endMin == null) return start;
  return `${start} - ${formatTime(endMin)}`;
}

/**
 * Format duration in minutes to a human string.
 * e.g. 45 → "45 min", 90 → "1h 30m"
 */
export function formatDuration(minutes) {
  if (!minutes || minutes <= 0) return '';
  if (minutes < 60) return `${minutes} min`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/** Map V3 parkKey to display name. */
export const PARK_NAMES = {
  MK: 'Magic Kingdom',
  DHS: 'Hollywood Studios',
  EPCOT: 'EPCOT',
  AK: 'Animal Kingdom',
  TL: 'Typhoon Lagoon',
  BB: 'Blizzard Beach',
  DS: 'Disney Springs',
  arrival: 'Arrival Day',
  departure: 'Departure Day',
  rest: 'Rest Day',
};

export function getParkDisplayName(parkKey) {
  return PARK_NAMES[parkKey] || parkKey || 'Park Day';
}

/** Map item type to display configuration. */
const TYPE_CONFIG = {
  ride:            { label: 'Ride',       style: 'bg-blue-50 text-blue-700 border-blue-200' },
  show:            { label: 'Show',       style: 'bg-violet-50 text-violet-700 border-violet-200' },
  dining:          { label: 'Dining',     style: 'bg-amber-50 text-amber-700 border-amber-200' },
  transfer:        { label: 'Transfer',   style: 'bg-gray-50 text-gray-500 border-gray-200' },
  buffer:          { label: 'Break',      style: 'bg-gray-50 text-gray-400 border-gray-100' },
  open_time:       { label: 'Free Time',  style: 'bg-gray-50 text-gray-500 border-gray-200' },
  flexible_time:   { label: 'Free Time',  style: 'bg-gray-50 text-gray-500 border-gray-200' },
  character_meet:  { label: 'Character',  style: 'bg-pink-50 text-pink-700 border-pink-200' },
  experience:      { label: 'Experience', style: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

export function getTypeConfig(type) {
  return TYPE_CONFIG[type] || { label: type || 'Activity', style: 'bg-gray-50 text-gray-600 border-gray-200' };
}

/** Result state display. */
export function getResultStateBadge(state) {
  switch (state) {
    case 'OK':       return { label: 'Great day', style: 'bg-green-50 text-green-700 border-green-200' };
    case 'DEGRADED': return { label: 'Good day',  style: 'bg-yellow-50 text-yellow-700 border-yellow-200' };
    case 'REJECTED': return { label: 'Needs review', style: 'bg-red-50 text-red-700 border-red-200' };
    default:         return null;
  }
}
